/**
 * MeetingNotesImportModal — paste a Zoom "Meeting Assets" summary, parse it into proposed thread
 * updates / new threads, review & edit each suggestion, then commit them to the meeting's minutes.
 *
 * Save/resume + review gate (2026-08-31): the parsed suggestions persist as a draft
 * (`meeting_notes_drafts`, one active row per meeting) the moment parsing succeeds, and every edit
 * autosaves — closing the modal and reopening it later (by the same person or someone else) picks
 * up exactly where it left off. Committing to threads is a deliberate second step: "Mark ready for
 * review" locks the draft and pings the meeting's Slack discussion; only from that
 * ready_for_review state can it be reopened for more edits or actually committed. This is a SOFT
 * gate (Matt, 2026-08-31) — nothing technically stops anyone from clicking Commit once it's ready
 * for review, but the modal no longer offers a one-click paste-to-thread shortcut. See
 * docs/MEETING_MINUTES.md.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import {
  parseMeetingNotes, getMeetingThreadIds, setMeetingThreads, markMeetingNotesImported,
  getMeetingNotesDraft, saveMeetingNotesDraft, submitMeetingNotesDraft, reopenMeetingNotesDraft,
  commitMeetingNotesDraft, discardMeetingNotesDraft, sendBackMeetingNotesDraft,
  notifyMeetingNotesReadyForReview, getDefaultMeetingNotesReviewer, getMeetingById,
} from '../services/meetings.js'
import { getTeamMembers } from '../services/members.js'
import { getContactNamesByIds, getContactTypesByIds } from '../services/contacts.js'
import CreateMeetingModal from './CreateMeetingModal.jsx'
import SearchSelect from './SearchSelect.jsx'
import TriageTiles from './TriageTiles.jsx'
import {
  createThreadsBatch, createThreadActivitiesBatch, updateThreadsBatch,
} from '../services/threads.js'
import { getRidingFor, decideThread } from '../services/passengers.js'
import { getMeetingAgenda, linkMeetingActivities, checkOffAgendaThreads, previewMeetingMinutes, postMeetingMinutes, addMinutesToClientHub, getMinutesAudience } from '../services/meetings.js'
import { alertDialog, confirmDialog, promptDialog } from './Dialog.jsx'
import { getProjectDetails } from '../services/projects.js'
import { startStallRecorder } from '../outline/stallRecorder.js'

const CC_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* Meeting day + N → 'YYYY-MM-DD' (local, no UTC shift). */
function plusDays(ymd, n) {
  let base
  if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) { const [y, m, d] = ymd.split('-').map(Number); base = new Date(y, m - 1, d) }
  else base = new Date()
  base.setDate(base.getDate() + n)
  return base.getFullYear() + '-' + String(base.getMonth() + 1).padStart(2, '0') + '-' + String(base.getDate()).padStart(2, '0')
}
/* Best-effort match of the AI's ball-in-court name to a meeting attendee (first-name, then substring). */
function matchAttendee(attendees, name) {
  if (!name) return null
  const fn = String(name).trim().toLowerCase().split(/\s+/)[0]
  if (!fn) return null
  return attendees.find(a => String(a.name).toLowerCase().split(/\s+/)[0] === fn)
      || attendees.find(a => String(a.name).toLowerCase().includes(fn))
      || null
}
function currentMemberName() {
  const f = (ctx.currentMember && ctx.currentMember.fields) || {}
  return f['Full Name'] || f['Name'] || f['Member Name'] || ''
}

const FALLBACK_CATS = [
  'Schedule and Progress', 'Design Approvals', 'Design Direction', 'Specialty Design', 'Submittals',
  'RFIs', 'PCOs and Change Orders', 'Permitting Items', 'Engineering Items', 'Contractor Items',
  'Inspector Items', 'Owner Items', 'Architect Items', 'Tendering Items', 'Misc. Items',
]
const getToken = () => Promise.resolve((typeof window !== 'undefined' && window.__clerkGetToken) ? window.__clerkGetToken() : null)

/* Textarea that starts fit to its content and grows/shrinks as the text changes. */
function AutoTextarea({ value, onChange, minHeight = 36, style, ...rest }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.max(minHeight, el.scrollHeight) + 'px'
  }, [value, minHeight])
  return (
    <textarea ref={ref} value={value} onChange={onChange}
      style={{ ...style, overflow: 'hidden', resize: 'none' }} {...rest} />
  )
}

/* Pick the follow-up person — limited to MEETING ATTENDEES (they own distributing the work). The
   pick sets Ball in Court on the thread, internal or external alike; it is NOT an assignment
   (RASCI was retired 2026-09-02 — crews are the only assignment mechanism, and they are set on
   the Flight Board only). Pre-seeded from the AI's ball-in-court guess. */
function FollowUpPicker({ value, attendees, onPick, disabled, invalid }) {
  if (!attendees.length) {
    return <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No attendees on this meeting — add attendees to assign follow-up.</span>
  }
  return (
    <SearchSelect
      value={value ? value.contactId : ''}
      disabled={disabled}
      onChange={v => { const a = attendees.find(x => x.id === v); onPick(a ? { contactId: a.id, name: a.name, isTeam: a.isTeam } : null) }}
      style={{ ...input, flex: 1, minWidth: 160, ...(invalid ? { border: '0.5px solid #B3261E' } : null) }}
      options={[{ value: '', label: '— pick a person —' }, ...attendees.map(a => ({ value: a.id, label: a.name + (a.isTeam ? ' (team)' : '') }))]}
    />
  )
}

/* projectId / zIndex are passed when the modal opens from the daily triage gate, outside the
   project's own page (no ctx.recordId, no meeting cache, and the gate's own scrim to sit above). */
export default function MeetingNotesImportModal({ meetingId, projectId: projectIdProp, projectName, zIndex, onClose, onCommitted }) {
  const [step, setStep] = useState('loading')    // loading | paste | review | committing
  // While committing, the work runs in this tab: leaving it half-way would leave threads written
  // and the minutes unposted. Warn before a reload or tab close (Matt, 2026-11-03).
  useEffect(() => {
    if (step !== 'committing') return
    const warn = e => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [step])
  const [text, setText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [items, setItems] = useState([])
  const [threads, setThreads] = useState([])
  const [attendees, setAttendees] = useState([])   // meeting attendees — the only follow-up candidates
  const [cats, setCats] = useState(FALLBACK_CATS)
  const [meetingDay, setMeetingDay] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  // Which of the meeting's threads are riding a milestone right now ({ <threadId>: { milestoneName } }).
  // Never saved into the draft — it goes stale the moment someone pairs a thread, so it is re-read
  // on every open and again at commit. See docs/MEETING_MINUTES.md "Passengers in the reviewer".
  const [riding, setRiding] = useState({})
  // The project's open milestones, from the parse: what a follow-up can ride (Matt, 2026-10-15:
  // a follow-up thread exists to be hitched to a milestone). Saved in the draft's context.
  const [milestones, setMilestones] = useState([])
  // Everyone the notes show at the meeting (from the parse). Anyone not on the attendee list is
  // flagged at the top: the drafter adds them before sending (Matt, 2026-10-15).
  const [people, setPeople] = useState([])
  // Open threads that involve someone AT this meeting but weren't on its agenda (api/_meetingAgenda.js,
  // Matt 2026-10-19): if one came up, add an item for it; otherwise it waits for the next meeting.
  const [offAgenda, setOffAgenda] = useState([])
  const [offState, setOffState] = useState('')   // '' | 'checking' | 'checked' | 'failed'
  // Off-agenda threads already handled outside the meeting (Matt, 2026-10-22): { <threadId>: { title,
  // note } }. They close on commit with the reason, never linked to this meeting, so never in its
  // minutes. Saved with the draft (context.handled).
  const [handled, setHandled] = useState({})
  // Which off-agenda threads ride a milestone right now (Matt, 2026-10-22): { <threadId>: { milestoneName } }.
  const [offRiding, setOffRiding] = useState({})
  const offIdsSig = offAgenda.map(t => t.id).join(',')
  useEffect(() => {
    if (!offIdsSig) { setOffRiding({}); return }
    let cancelled = false
    getRidingFor(offIdsSig.split(',')).then(r => { if (!cancelled) setOffRiding((r && r.byThread) || {}) }).catch(() => {})
    return () => { cancelled = true }
  }, [offIdsSig])
  // An internal meeting (nobody from outside the team) posts on commit but skips the Client Hub.
  const [internalMeeting, setInternalMeeting] = useState(null)
  // Who the minutes reach (Matt, 2026-10-22): the Client Hub only when a client attended; outside
  // attendees with no Hub get the PDF by email. null until read.
  const [audience, setAudience] = useState(null)
  function loadOffAgenda() {
    getMeetingAgenda(meetingId).then(a => { setOffAgenda((a && a.suggestedThreads) || []); if (a && typeof a.internal === 'boolean') setInternalMeeting(a.internal) }).catch(() => {})
    getMinutesAudience(meetingId, { getToken }).then(setAudience).catch(() => setAudience(null))
  }
  // Read the pasted notes for each off-agenda thread: did it come up, the line, a ready note.
  const offCheckedFor = useRef('')
  useEffect(() => {
    if (!offAgenda.length || !String(text || '').trim()) return
    const sig = offAgenda.map(t => t.id).join(',') + '|' + String(text).length
    if (offCheckedFor.current === sig) return
    offCheckedFor.current = sig
    setOffState('checking')
    checkOffAgendaThreads(meetingId, text).then(r => { setOffAgenda(r); setOffState('checked') }).catch(() => setOffState('failed'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offAgenda.length, text])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (meetingId) loadOffAgenda() }, [meetingId])
  // The rest of the saved context (milestones, the list a re-run replaced) carried through every
  // save, since a save writes the whole context.
  const ctxExtraRef = useRef({})

  // Draft (save/resume + review gate) state.
  const [draftId, setDraftId] = useState(null)
  const [status, setStatus] = useState('draft')     // 'draft' | 'ready_for_review'
  const [reviewNote, setReviewNote] = useState('')
  // Who reviews it once submitted. Pre-filled with the project's Client Manager; required.
  const [reviewer, setReviewer] = useState(null)        // { memberId, name } | null
  const reviewerRef = useRef(null)                      // latest pick, read by the save (timers hold stale closures)
  const [savedReviewerName, setSavedReviewerName] = useState('')
  const [savedReviewerId, setSavedReviewerId] = useState('')
  const [draftedBy, setDraftedBy] = useState('')        // who sent it for review (the send-back target)
  const [draftedById, setDraftedById] = useState('')
  // Once sent back, only the person the sender named can approve it (database/303) — the sender
  // themself unless they picked someone else at send-back. The drafter's only way forward is to them.
  const [sentBackBy, setSentBackBy] = useState(null)   // { memberId, name } | null
  const [team, setTeam] = useState([])                  // [{ id, name }] — reviewer candidates

  const projectId = projectIdProp || ctx.recordId || wf.zoomProjectId || ''
  const memberId = (ctx.currentMember && ctx.currentMember.id) || wf.currentMemberId || null
  const memberName = currentMemberName()
  // The reviewer edits a submitted import in place — no "reopen" step (Matt, 2026-09-23). It stays
  // locked only for everyone else while it waits (the submitter can pull it back with Reopen).
  const reviewerState = reviewer
  const isReviewer = status === 'ready_for_review' && !!memberId && savedReviewerId === memberId
  // Sent back to the drafter, and YOU are who it returns to (Brandon, 2026-10-29: Matt sent Gian's
  // notes back "Brandon should review", Brandon opened them before Gian did and was shown the
  // drafter's screen). You are the approver, so you may take it over and commit it yourself.
  const isWaitingApprover = status === 'draft' && !!memberId && !!sentBackBy && sentBackBy.memberId === memberId && draftedById !== memberId
  const readOnly = status === 'ready_for_review' && !isReviewer
  const saveTimerRef = useRef(null)
  const pendingRef = useRef(null)                       // item list waiting to be saved, if any
  // What the header says about saving: idle | pending | saving | saved (at) | error (msg).
  const [saveState, setSaveState] = useState({ s: 'idle', at: null })

  /* Returns the riding map, or null when it could not be read (commit treats null as "stop"). */
  async function loadRiding(threadList) {
    const ids = (threadList || []).map(t => t.id).filter(Boolean)
    if (!ids.length) { setRiding({}); return {} }
    try {
      const res = await getRidingFor(ids)
      const map = (res && res.byThread) || {}
      setRiding(map)
      return map
    } catch (_) { return null }
  }

  // Diagnostic: record main-thread stalls while this is open (Matt reported hangs; see stallRecorder.js).
  useEffect(() => { startStallRecorder('meeting-import:' + meetingId); return () => { window.__ajStallWhere = '' } }, [meetingId])

  // Which meeting this is, for the header (Matt, 2026-09-23: "so I don't have to go back a modal to
  // be reminded which meeting I am reviewing"). From the page's meeting cache when opened on the
  // project, else one read of the meeting (opened from triage).
  const [meetingInfo, setMeetingInfo] = useState(null)   // { type, date, projectNumber }
  const shape = m => {
    const f = (m && m.fields) || {}
    const start = f['Meeting Date'] ? new Date(f['Meeting Date']) : null
    const end = f['Meeting End'] ? new Date(f['Meeting End']) : null
    const t = d => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })
    return {
      type: f['Type'] || '', date: f['Meeting Date String'] || '',
      time: start ? t(start) + (end ? ' to ' + t(end) : '') : '',
      location: f['Location'] || '',
      projectId: (f['Project'] || [])[0] || '',
      projectNumber: (f['Project Number (from Project)'] || [])[0] || '',
    }
  }
  // The meeting's own project name, so a meeting filed under the wrong project is obvious.
  const [meetingProjectName, setMeetingProjectName] = useState('')
  const meetingProjectId = meetingInfo && meetingInfo.projectId
  useEffect(() => {
    if (!meetingProjectId) return
    let cancelled = false
    getProjectDetails(meetingProjectId).then(r => { if (!cancelled && r && r.fields) setMeetingProjectName(r.fields['Project Name'] || '') }).catch(() => {})
    return () => { cancelled = true }
  }, [meetingProjectId])
  useEffect(() => {
    let cancelled = false
    const cached = (ctx.meetingCache || []).find(m => m.id === meetingId)
    if (cached) setMeetingInfo(shape(cached))
    else getMeetingById(meetingId).then(m => { if (!cancelled && m) setMeetingInfo(shape(m)) }).catch(() => {})
    return () => { cancelled = true }
  }, [meetingId])

  // Resume an existing draft on open, if one exists.
  useEffect(() => {
    let cancelled = false
    getMeetingNotesDraft(meetingId, { getToken }).then(res => {
      if (cancelled) return
      const d = res && res.draft
      if (!d) { setStep('paste'); return }
      setDraftId(d.id)
      setStatus(d.status)
      setReviewNote(d.review_note || '')
      setSavedReviewerName(d.reviewer_name || '')
      setSavedReviewerId(d.reviewer_member_id || '')
      setDraftedBy(d.submitted_by_name || d.created_by_name || '')
      setDraftedById(d.submitted_by_member_id || d.created_by_member_id || '')
      if (d.sent_back_by_member_id) {
        const sb = { memberId: d.sent_back_by_member_id, name: d.sent_back_by_name || '' }
        setSentBackBy(sb)
        if (d.status === 'draft') { reviewerRef.current = sb; setReviewer(sb) }
      }
      // A draft keeps the reviewer you picked (saved with it); it wins over the Client Manager default.
      // Once sent back, the reviewer IS whoever it returns to: reviewer_member_id still holds the first
      // reviewer (who may have handed it on), and submitting to them is refused (409).
      if (d.status === 'draft' && d.reviewer_member_id && !d.sent_back_by_member_id) { const rv = { memberId: d.reviewer_member_id, name: d.reviewer_name || '' }; reviewerRef.current = rv; setReviewer(rv) }
      if (d.updated_at) setSaveState({ s: 'saved', at: new Date(d.updated_at) })
      setText(d.raw_text || '')
      setMeetingDay(d.meeting_day || '')
      const c = d.context || {}
      setThreads(c.threads || [])
      loadRiding(c.threads || [])
      setAttendees(c.attendees || [])
      setMilestones(c.milestones || [])
      setPeople(c.people || [])
      { const { threads: _t, attendees: _a, categories: _c, ...rest } = c; ctxExtraRef.current = rest; setHandled(rest.handled || {}) }
      setCats((c.categories && c.categories.length) ? c.categories : FALLBACK_CATS)
      if (d.suggestions && d.suggestions.length) { setItems(d.suggestions); setStep('review') }
      else setStep('paste')
    }).catch(() => setStep('paste'))
    return () => { cancelled = true; if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId])

  // Reviewer candidates + the default (the project's Client Manager). Loaded once per open.
  useEffect(() => {
    let cancelled = false
    getTeamMembers().then(list => { if (!cancelled) setTeam(list || []) }).catch(() => {})
    if (projectId) {
      getDefaultMeetingNotesReviewer(projectId, { getToken }).then(res => {
        if (cancelled || !res || !res.reviewer || !res.reviewer.memberId) return
        setReviewer(r => { const next = r || res.reviewer; reviewerRef.current = next; return next })
      }).catch(() => {})
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId])

  /* Save one version of the item list and report it on screen (Matt, 2026-09-23: "how do I know
     if my drafts are saved?" — before this a failed autosave was swallowed silently). Resolves
     true when the server accepted it. */
  async function persist(list) {
    setSaveState({ s: 'saving', at: null })
    let res
    try {
      res = await saveMeetingNotesDraft({
        id: draftId, meetingId, projectId, rawText: text, suggestions: list, meetingDay, editorMemberId: memberId,
        ...(status === 'draft' ? { reviewerMemberId: (reviewerRef.current && reviewerRef.current.memberId) || null, reviewerName: (reviewerRef.current && reviewerRef.current.name) || null } : {}),
        context: { ...ctxExtraRef.current, threads, attendees, categories: cats },
      }, { getToken })
    } catch (e) { res = { error: (e && e.message) || 'Save failed.' } }
    if (!res || res.error || !res.draft) { setSaveState({ s: 'error', at: null, msg: (res && res.error) || 'Save failed.' }); return false }
    pendingRef.current = null
    setSaveState({ s: 'saved', at: new Date() })
    return true
  }

  /* Debounced autosave — the mechanism behind "leave it and come back to it later." No-ops once
     the draft is locked (ready_for_review) or before it's been created (pre-parse). */
  function scheduleSave(nextItems) {
    if (!draftId || readOnly) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    pendingRef.current = nextItems
    setSaveState(st => (st.s === 'error' ? st : { s: 'pending', at: st.at }))
    saveTimerRef.current = setTimeout(() => { saveTimerRef.current = null; persist(nextItems) }, 900)
  }

  /* Write anything not yet saved (a pending edit, or a save that failed). True when nothing is lost. */
  async function flushSave() {
    if (!draftId || readOnly) return true
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    if (pendingRef.current || saveState.s === 'error') return persist(pendingRef.current || items)
    return true
  }

  /* Closing saves the last edit first; if that fails, ask before throwing the edits away. */
  async function handleClose() {
    if (step === 'review' && !(await flushSave())) {
      if (!(await confirmDialog({ title: 'Your last changes did not save', message: 'Close anyway and lose them? Choose Cancel to stay and try again.', confirmLabel: 'Close anyway', danger: true }))) return
    }
    onClose && onClose()
  }

  /* The meeting's own header (type, date, attendees, ...) is editable from here (Matt, 2026-10-06:
     the parse surfaced missing attendees and there was no way to fix them before committing).
     It edits the real Meeting record through the same CreateMeetingModal the meetings list uses,
     then re-reads it: the attendee list is the follow-up picker's only candidates and the meeting
     day dates every activity the commit writes, so both are refreshed into the draft. */
  const [editingMeeting, setEditingMeeting] = useState(false)
  async function onMeetingSaved() {
    setEditingMeeting(false)
    loadOffAgenda()
    let m = null
    try { m = await getMeetingById(meetingId) } catch (_) { m = null }
    if (!m) return
    setMeetingInfo(shape(m))
    if (Array.isArray(ctx.meetingCache)) {
      ctx.meetingCache = ctx.meetingCache.map(x => x.id === m.id ? m : x)
      window.dispatchEvent(new Event('outline-render'))
    }
    const f = m.fields || {}
    const ids = f['Attendees'] || []
    const [names, types] = await Promise.all([getContactNamesByIds(ids), getContactTypesByIds(ids)])
    const atts = ids.map(id => ({ id, name: names[id] || 'Attendee', isTeam: String(types[id] || '').toLowerCase() === 'team' }))
    const day = (f['Meeting Date'] || '').slice(0, 10) || meetingDay
    setAttendees(atts)
    setMeetingDay(day)
    if (!items.length) return
    // Drop follow-up picks whose person is no longer an attendee; fill empty ones the parser
    // named (its ball-in-court guess) now that they may be on the list.
    const attIds = new Set(atts.map(a => a.id))
    const next = items.map(it => {
      if (it.fuContact && !attIds.has(it.fuContact.contactId)) it = { ...it, fuContact: null }
      if (it.followUp && !it.fuContact && it.ballInCourt) {
        const a = matchAttendee(atts, it.ballInCourt)
        if (a) it = { ...it, fuContact: { contactId: a.id, name: a.name, isTeam: a.isTeam } }
      }
      return it
    })
    setItems(next)
    if (!draftId || readOnly) return
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    setSaveState({ s: 'saving', at: null })
    let res
    try {
      res = await saveMeetingNotesDraft({
        id: draftId, meetingId, projectId, rawText: text, suggestions: next, meetingDay: day, editorMemberId: memberId,
        ...(status === 'draft' ? { reviewerMemberId: (reviewerRef.current && reviewerRef.current.memberId) || null, reviewerName: (reviewerRef.current && reviewerRef.current.name) || null } : {}),
        context: { ...ctxExtraRef.current, threads, attendees: atts, categories: cats },
      }, { getToken })
    } catch (e) { res = { error: (e && e.message) || 'Save failed.' } }
    if (!res || res.error || !res.draft) { pendingRef.current = next; setSaveState({ s: 'error', at: null, msg: (res && res.error) || 'Save failed.' }) }
    else { pendingRef.current = null; setSaveState({ s: 'saved', at: new Date() }) }
  }

  function addItem() {
    setItems(prev => {
      const nextId = prev.reduce((m, it) => Math.max(m, Number(it._id) || 0), -1) + 1
      const next = [...prev, {
        _id: nextId, manual: true, accept: true, kind: 'new', threadId: '', title: '',
        category: 'Misc. Items', note: '', followUp: false, fuContact: null, ballInCourt: '', confidence: '',
      }]
      scheduleSave(next)
      return next
    })
  }
  // SPLIT (Matt, 2026-10-22): one ball, one court. When an item puts something on two people, it
  // becomes two threads, each with its own court. The copy lands right below as a NEW thread (a
  // second court needs its own thread), with the same title and note to trim; both cite this meeting.
  function splitItem(id) {
    setItems(prev => {
      const i = prev.findIndex(it => it._id === id)
      if (i < 0) return prev
      const src = prev[i]
      const nextId = prev.reduce((m, it) => Math.max(m, Number(it._id) || 0), -1) + 1
      const srcTitle = src.kind === 'update' ? ((threads.find(t => t.id === src.threadId) || {}).title || src.title || '') : (src.title || '')
      const copy = {
        _id: nextId, manual: true, accept: true, kind: 'new', threadId: '', title: srcTitle,
        category: src.category || 'Misc. Items', note: src.note || '', followUp: true, fuContact: null, ballInCourt: '', confidence: '',
        splitFrom: src._id,
      }
      const next = [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)]
      scheduleSave(next)
      return next
    })
  }
  // "It came up" on an off-agenda thread: an update item for that thread, ready for its note.
  /* Mark an off-agenda thread handled outside the meeting (note) or clear it (null); saved with the draft. */
  function setHandledFor(t, note) {
    const next = { ...handled }
    if (note) next[t.id] = { title: t.title, note }; else delete next[t.id]
    ctxExtraRef.current = { ...ctxExtraRef.current, handled: next }
    setHandled(next)
    persist(items)
  }
  async function askHandled(t) {
    const note = await promptDialog({ title: 'Already handled', message: `“${t.title}” closes when you commit, with this line on the thread. It won’t appear in these minutes.`, placeholder: 'What happened, e.g. Nick emailed the MEP estimate Oct 20', required: true, confirmLabel: 'Close on commit' })
    if (note && note.trim()) setHandledFor(t, note.trim())
  }
  function addItemFor(t) {
    setItems(prev => {
      if (prev.some(it => it.threadId === t.id)) return prev
      const nextId = prev.reduce((m, it) => Math.max(m, Number(it._id) || 0), -1) + 1
      const next = [...prev, {
        _id: nextId, manual: true, accept: true, kind: 'update', threadId: t.id, title: t.title,
        category: t.type || 'Misc. Items', note: t.note || '', followUp: false, fuContact: null, ballInCourt: '', confidence: '',
        // The context the off-agenda list showed, carried onto the item (Matt, 2026-10-22: by the time
        // he reached the item he'd forgotten what the thread was about).
        prior: { about: t.about || '', court: t.court || '', lastMeeting: t.lastMeeting || '', quote: t.quote || '' },
      }]
      scheduleSave(next)
      return next
    })
  }
  function removeItem(id) {
    setItems(prev => {
      const next = prev.filter(it => it._id !== id)
      scheduleSave(next)
      return next
    })
  }

  async function doParse() {
    if (!text.trim()) return
    setParsing(true)
    const res = await parseMeetingNotes(meetingId, text, { getToken })
    setParsing(false)
    if (!res || res.error) { await alertDialog({ title: 'Could not parse', message: (res && res.error) || 'No response.' }); return }
    // Use the AI's per-item follow-up judgment (true = outstanding action; false = info-only) and
    // pre-pick the follow-up person from the attendees by matching the AI's ball-in-court guess.
    // No per-item date: threads carry no target date (2026-09-10); the plane they ride does.
    const atts = res.attendees || []
    const sugg = (res.suggestions || []).map((s, i) => {
      const fu = s.followUp === true
      const m = fu && !s.fuContact ? matchAttendee(atts, s.ballInCourt) : null
      return {
        ...s, _id: i, accept: true,
        followUp: fu,
        fuContact: s.fuContact || (m ? { contactId: m.id, name: m.name, isTeam: m.isTeam } : null),
      }
    })
    if (!sugg.length) { await alertDialog({ title: 'Nothing to import', message: 'The parser found no actionable items in that summary.' }); return }
    const day = res.meetingDay || meetingDay
    const cts = res.categories && res.categories.length ? res.categories : FALLBACK_CATS
    setItems(sugg)
    setThreads(res.threads || [])
    loadRiding(res.threads || [])
    setAttendees(atts)
    setMilestones(res.milestones || [])
    setPeople(res.people || [])
    ctxExtraRef.current = { milestones: res.milestones || [], people: res.people || [] }
    setCats(cts)
    setMeetingDay(day)
    setStep('review')

    // Persist immediately — this is what makes the paste survive closing the modal.
    const saveRes = await saveMeetingNotesDraft({
      id: draftId, meetingId, projectId, rawText: text, suggestions: sugg, meetingDay: day,
      context: { milestones: res.milestones || [], people: res.people || [], threads: res.threads || [], attendees: atts, categories: cts },
      createdByName: memberName, createdByMemberId: memberId,
    }, { getToken })
    if (saveRes && saveRes.draft) { setDraftId(saveRes.draft.id); setStatus(saveRes.draft.status); setSaveState({ s: 'saved', at: new Date() }) }
    else setSaveState({ s: 'error', at: null, msg: (saveRes && saveRes.error) || 'Save failed.' })
  }

  function patch(id, fields) {
    setItems(prev => {
      const next = prev.map(it => it._id === id ? { ...it, ...fields } : it)
      scheduleSave(next)
      return next
    })
  }

  /* Accepted items marked follow-up with nobody picked. A follow-up with no one in court lands in
     nobody's triage, so it can't leave the reviewer that way. */
  async function blockOnMissingPerson(list) {
    // Included items that can't be written as they stand: an update with no thread picked (commit
    // would otherwise quietly create an untitled new thread), a new thread with no title, or no
    // note at all (commit silently drops those). Skip them or finish them first.
    const incomplete = list.filter(it => it.accept && (
      !String(it.note || '').trim() || (it.kind === 'update' ? !it.threadId : !String(it.title || '').trim())))
    if (incomplete.length) {
      const lines = incomplete.map(it => '• ' + ((it.kind === 'update' ? (threads.find(t => t.id === it.threadId) || {}).title : it.title) || 'Untitled item') + ': '
        + (!String(it.note || '').trim() ? 'no discussion note' : it.kind === 'update' ? 'no thread picked' : 'no title')).join('\n')
      await alertDialog({ title: 'Finish or skip these items', message: `${incomplete.length === 1 ? 'This item is' : 'These items are'} included but not complete:\n\n${lines}` })
      return true
    }
    const missing = list.filter(it => it.accept && it.note.trim() && it.followUp && !it.fuContact)
    if (!missing.length) return false
    const names = missing.map(it => '• ' + (it.kind === 'update'
      ? ((threads.find(t => t.id === it.threadId) || {}).title || 'Untitled thread')
      : (it.title || 'Untitled topic'))).join('\n')
    await alertDialog({
      title: 'Pick a follow-up person',
      message: `${missing.length === 1 ? 'This item is' : 'These items are'} marked "Follow-up required" with nobody picked. Pick who follows up, or untick the box.\n\n${names}`,
    })
    return true
  }

  async function doSubmit(rvPicked, cc = []) {
    // Sent back: it can only go to the person it returns to (the server refuses anyone else).
    const reviewer = sentBackBy || rvPicked || reviewerRef.current || reviewerState
    if (!draftId) { await alertDialog({ title: 'Not saved yet', message: 'This draft has not finished saving — wait a moment and try again.' }); return }
    const acceptedCount = items.filter(it => it.accept && it.note.trim()).length
    if (!acceptedCount) { await alertDialog({ title: 'Nothing included', message: 'Include at least one item with a discussion note first.' }); return }
    if (await blockOnMissingPerson(items)) return
    const missingNow = people.filter(n => !matchAttendee(attendees, n))
    if (missingNow.length && !(await confirmDialog({
      title: 'Attendees missing',
      message: 'The notes show ' + missingNow.join(', ') + ' at this meeting, but the attendee list does not have them. Add them with Edit meeting info first, unless they really were not there.',
      confirmLabel: 'Send anyway', cancelLabel: 'Go back and add them',
    }))) return
    if (!reviewer || !reviewer.memberId) { await alertDialog({ title: 'Pick a draft reviewer', message: 'Choose who should review this import before sending it.' }); return }
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    // Save the latest edits before locking; never lock or commit over a failed save.
    if (!(await persist(items))) { await alertDialog({ title: 'Could not save', message: 'Your latest changes did not save, so nothing was sent. Try again in a moment.' }); return }
    // Reviewing your own import adds a step without a second pair of eyes (Matt, 2026-09-23):
    // when the reviewer is you, commit straight away.
    if (memberId && reviewer.memberId === memberId && !sentBackBy) { await doCommit(cc); return }
    const res = await submitMeetingNotesDraft(draftId, { submittedByName: memberName, submittedByMemberId: memberId, reviewer }, { getToken })
    if (!res || res.error) { await alertDialog({ title: 'Could not submit', message: (res && res.error) || 'Try again.' }); return }
    setStatus('ready_for_review')
    setReviewer(reviewer); setSavedReviewerName(reviewer.name || '')
    notifyMeetingNotesReadyForReview(meetingId, { submitterName: memberName, itemCount: acceptedCount }, { getToken })
    onCommitted && onCommitted('submitted')
    onClose && onClose()
  }

  /* Reopen unlocks the import for whoever clicked it — no note asked for (Matt, 2026-09-23: a
     reviewer who wants a change usually just makes it). The reopener now owns finishing it, so
     it is their triage it shows up in if left, not the sender's. */
  async function doReopen() {
    const res = await reopenMeetingNotesDraft(draftId, { reopenedByName: memberName, reopenedByMemberId: memberId }, { getToken })
    if (!res || res.error) { await alertDialog({ title: 'Could not reopen', message: (res && res.error) || 'Try again.' }); return }
    setStatus('draft')
    setReviewNote('')
  }

  /* Send it back to whoever drafted it to fix themselves (Matt, 2026-10-15: "not fix his errors
     and ask him to do it so they don't keep happening"). The note says what to fix; it lands in
     their daily triage. */
  async function doSendBack(tileNote, returnTo) {
    const who = draftedBy || 'Whoever sent it to you'
    const note = tileNote != null ? tileNote : await promptDialog({
      title: 'Send back to drafter',
      message: 'Say what needs fixing. ' + who + ' sees this note the next time they open the app.',
      placeholder: 'e.g. Add the missing attendees and their contacts. Or: wrong project, discard this and start the minutes under the right one.',
      multiline: true, required: true, confirmLabel: 'Send back',
    })
    if (note === null || !String(note).trim()) return
    if (!(await flushSave())) { await alertDialog({ title: 'Could not save', message: 'Your latest changes did not save, so it was not sent back. Try again in a moment.' }); return }
    // returnTo: who approves it once fixed (Matt, 2026-10-28) — you, unless you pick someone else.
    const rt = returnTo && returnTo !== memberId ? team.find(m => m.id === returnTo) : null
    const res = await sendBackMeetingNotesDraft(draftId, {
      note, sentBackByName: memberName, sentBackByMemberId: memberId,
      ...(rt ? { returnToMemberId: rt.id, returnToName: rt.name } : {}),
    }, { getToken })
    if (!res || res.error) { await alertDialog({ title: 'Could not send back', message: (res && res.error) || 'Try again.' }); return }
    onCommitted && onCommitted('sentBack')
    onClose && onClose()
  }

  async function doDiscard(confirmed) {
    if (!confirmed && !(await confirmDialog({ title: 'Discard this import?', message: 'Delete this draft and start over from a fresh paste. Nothing has been written to threads yet.', confirmLabel: 'Discard draft', danger: true }))) return
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    pendingRef.current = null
    try { await discardMeetingNotesDraft(draftId, { getToken }) } catch (_) {}
    onCommitted && onCommitted('discarded')
    onClose && onClose()
  }

  /* The draft as the minutes will read once committed (Matt, 2026-10-20: "important to see how they
     read"): the same renderer the posted PDF uses, with the included items laid over the meeting
     (api/meeting-minutes-pdf.js `overlay`). Nothing is written. */
  function minutesOverlay() {
    const court = s => s.followUp ? ((s.fuContact && s.fuContact.name) || '') : 'Info'
    const inc = items.filter(it => it.accept && it.note.trim())
    return {
      updates: inc.filter(s => s.kind === 'update' && s.threadId).map(s => ({ threadId: s.threadId, note: s.note.trim(), court: court(s) })),
      news: inc.filter(s => !(s.kind === 'update' && s.threadId)).map(s => ({ title: (s.title || 'Untitled topic').trim(), type: s.category || 'Misc. Items', note: s.note.trim(), court: court(s) })),
    }
  }
  const [previewing, setPreviewing] = useState(false)
  async function doPreviewMinutes() {
    // Open the tab inside the click (no popup block), then point it at the PDF once it renders.
    const tab = window.open('', '_blank')
    if (tab) { try { tab.document.write('<!doctype html><meta charset="utf-8"><title>Preparing the minutes</title><body style="margin:0;font-family:Arial,sans-serif;color:#07272D;background:#f4f7f8;display:flex;align-items:center;justify-content:center;height:100vh"><div>Preparing the minutes...</div></body>'); tab.document.close() } catch (_) {} }
    setPreviewing(true)
    const res = await previewMeetingMinutes(meetingId, { getToken, overlay: minutesOverlay() })
    setPreviewing(false)
    if (res && res.pdfUrl) { if (tab) tab.location = res.pdfUrl; else window.open(res.pdfUrl, '_blank') }
    else { if (tab) { try { tab.close() } catch (_) {} } await alertDialog({ title: 'Preview unavailable', message: (res && res.error) || 'Could not prepare the minutes.' }) }
  }
  const nameList = a => (a.length <= 1 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1])
  const postsWhere = (() => {
    if (!audience) return 'Then the minutes post: every attendee is emailed the PDF, and a copy goes on the Client Hub if a client was at the meeting.'
    const parts = []
    if (audience.email.length) parts.push(`${nameList(audience.email.map(x => x.name))} ${audience.email.length === 1 ? 'is' : 'are'} emailed the PDF, with five working days to request a revision`)
    if (audience.clientPresent) parts.push('a copy goes on the Client Hub (no separate notice)')
    if (audience.noEmail.length) parts.push(`${nameList(audience.noEmail)} ${audience.noEmail.length === 1 ? 'has' : 'have'} no email on file, so send ${audience.noEmail.length === 1 ? 'theirs' : 'theirs'} by hand`)
    return 'Then the minutes post: ' + parts.join('; ') + '.'
  })()

  /* CC: extra people copied on the minutes email (Brandon, 2026-10-30: the old Post & email screen
     had this and the commit lost it). Typed as a list, commas or spaces between. */
  const parseCc = v => Array.from(new Set(String(v || '').split(/[,;\s]+/).map(x => x.trim()).filter(Boolean)))
  const ccBad = v => parseCc(v).filter(a => !CC_EMAIL_RE.test(a))
  const ccField = (val, onChange) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)' }}>CC (optional): anyone else who should get a copy</div>
      <input className="aj-tt-text" style={{ minHeight: 0 }} value={val} placeholder="name@example.com, another@example.com"
        onChange={e => onChange(e.target.value)} />
      {ccBad(val).length > 0 && <div style={{ fontSize: 12, color: '#b42318' }}>Not an email address: {ccBad(val).join(', ')}</div>}
    </div>
  )
  const commitStep = () => ({ kind: 'custom', title: 'Commit and post the minutes',
    body: `Writes ${commitWhat()}, and logs them on the meeting. ${postsWhere}`,
    defaultValue: '',
    ready: v => ccBad(v).length === 0,
    render: (v, set) => ccField(typeof v === 'string' ? v : '', set),
    confirm: v => parseCc(v).length ? `Commit and post · cc ${parseCc(v).length}` : 'Commit and post', confirmIcon: 'did' })
  function commitWhat() {
    const n = items.filter(i => i.accept && i.note.trim()).length
    const nNew = items.filter(i => i.accept && i.note.trim() && !(i.kind === 'update' && i.threadId)).length
    const nH = Object.keys(handled).length
    return `${n} item${n === 1 ? '' : 's'}: ${nNew} new thread${nNew === 1 ? '' : 's'}, ${n - nNew} update${n - nNew === 1 ? '' : 's'} to existing ones`
      + (nH ? `, and closes ${nH} thread${nH === 1 ? '' : 's'} already handled outside the meeting (not in these minutes)` : '')
  }

  function decisionOptions() {
    const n = items.filter(i => i.accept && i.note.trim()).length
    const nNew = items.filter(i => i.accept && i.note.trim() && !(i.kind === 'update' && i.threadId)).length
    const nH = Object.keys(handled).length
    const what = `${n} item${n === 1 ? '' : 's'}: ${nNew} new thread${nNew === 1 ? '' : 's'}, ${n - nNew} update${n - nNew === 1 ? '' : 's'} to existing ones`
      + (nH ? `, and closes ${nH} thread${nH === 1 ? '' : 's'} already handled outside the meeting (not in these minutes)` : '')
    const first = (x) => String(x || '').split(' ')[0]
    if (isReviewer) {
      const opts = [{ key: 'commit', icon: 'did', label: `Commit ${n}`, sub: 'Looks right; write them to threads', tone: 'primary',
        step: commitStep(),
        run: (v) => doCommit(parseCc(v)) }]
      if (draftedBy && draftedById !== memberId) {
        // Who approves it once fixed: you by default, or someone else (e.g. you weren't at the meeting).
        const nextPeople = team.filter(m => m.id !== draftedById).map(m => ({ id: m.id, name: m.id === memberId ? m.name + ' (me)' : m.name }))
        const nextName = (v) => v.to === memberId ? 'you' : first((team.find(m => m.id === v.to) || {}).name)
        opts.push({ key: 'back', icon: 'sendback', label: 'Send back', sub: `${first(draftedBy)} fixes it`, tone: 'caution',
          step: { kind: 'custom', title: `Send back to ${first(draftedBy)}`,
            body: `Say what needs fixing. ${draftedBy} gets a Slack message and it is in their triage until it is fixed.`,
            defaultValue: { note: '', to: memberId },
            ready: (v) => !!(v && String(v.note || '').trim() && v.to),
            render: (v, set) => {
              const val = v && typeof v === 'object' ? v : { note: '', to: memberId }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <textarea className="aj-tt-text" autoFocus value={val.note} placeholder="e.g. I wasn't at this meeting. Take me off the attendees."
                    onChange={e => set({ ...val, note: e.target.value })} />
                  <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)' }}>Once fixed, it goes to:</div>
                  <div className="aj-tt-people">
                    {nextPeople.map(p => (
                      <button key={p.id} type="button" className={'aj-tt-person' + (val.to === p.id ? ' on' : '')} onClick={() => set({ ...val, to: p.id })}>
                        <span className="aj-tt-av">{String(p.name || '').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}</span>{p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )
            },
            confirm: (v) => v && v.to && v.to !== memberId ? `Send back · then to ${nextName(v)}` : `Send back to ${first(draftedBy)}`,
            confirmIcon: 'sendback' },
          run: (v) => doSendBack(String((v && v.note) || '').trim(), v && v.to),
          done: (v) => v && v.to && v.to !== memberId
            ? `Sent back to ${first(draftedBy)}. Once fixed it goes to ${nextName(v)}.`
            : `Sent back to ${first(draftedBy)}.` })
      }
      return opts
    }
    const opts = []
    if (isWaitingApprover) opts.push({ key: 'commit', icon: 'did', label: `Commit ${n}`, sub: `You approve it; no need to wait for ${first(draftedBy) || 'the drafter'}`, tone: 'primary',
      step: commitStep(),
      run: (v) => doCommit(parseCc(v)) })
    else if (sentBackBy) opts.push({ key: 'return', icon: 'sendback', label: 'Fixed it', sub: `Return to ${first(sentBackBy.name) || 'the reviewer'} for approval`, tone: 'primary',
      step: { title: `Return to ${sentBackBy.name || 'the reviewer'}`, body: `Sends ${what} back for approval. Nothing is written to threads until they approve it.`, confirm: () => `Send back to ${first(sentBackBy.name) || 'reviewer'}`, confirmIcon: 'sendback' },
      run: () => doSubmit() })
    else opts.push({ key: 'send', icon: 'handoff', label: 'Send for review', sub: 'Someone checks before it is written', tone: 'primary',
      step: { kind: 'custom', defaultValue: { to: reviewer ? reviewer.memberId : '', cc: '' },
        title: 'Who reviews it?', body: `They get a Slack message and it is in their triage. Pick yourself to commit ${what} now and post the minutes.`,
        ready: (v) => !!(v && v.to) && (v.to !== memberId || ccBad(v.cc).length === 0),
        render: (v, set) => {
          const val = v && typeof v === 'object' ? v : { to: '', cc: '' }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="aj-tt-people">
                {team.map(m => (
                  <button key={m.id} type="button" className={'aj-tt-person' + (val.to === m.id ? ' on' : '')} onClick={() => set({ ...val, to: m.id })}>
                    <span className="aj-tt-av">{String(m.name || '').split(/\s+/).map(x => x[0]).slice(0, 2).join('').toUpperCase()}</span>{m.id === memberId ? m.name + ' (me)' : m.name}
                  </button>
                ))}
              </div>
              {val.to === memberId && ccField(val.cc, c => set({ ...val, cc: c }))}
            </div>
          )
        },
        confirm: (v) => !v || !v.to ? 'Pick a reviewer' : v.to === memberId ? (parseCc(v.cc).length ? `Commit and post · cc ${parseCc(v.cc).length}` : 'Commit and post') : `Send to ${first((team.find(m => m.id === v.to) || {}).name)}`, confirmIcon: 'handoff' },
      run: (v) => { const m = team.find(x => x.id === (v && v.to)); const rv = m ? { memberId: m.id, name: m.name } : null; reviewerRef.current = rv; return doSubmit(rv, m && m.id === memberId ? parseCc(v.cc) : []) } })
    opts.push({ key: 'discard', icon: 'dayoff', label: 'Discard', sub: 'Start over from a fresh paste', tone: 'caution',
      step: { title: 'Discard this import', body: 'Deletes this draft. Nothing has been written to threads yet.', confirm: () => 'Discard the draft', confirmIcon: 'dayoff' },
      run: () => doDiscard(true) })
    return opts
  }

  async function doCommit(cc = []) {
    const accepted = items.filter(it => it.accept && it.note.trim())
    const handledList = Object.entries(handled).map(([threadId, h]) => ({ threadId, ...h }))
    if (!accepted.length && !handledList.length) { await alertDialog({ title: 'Nothing included', message: 'Include at least one item to import.' }); return }
    if (await blockOnMissingPerson(items)) return
    // The reviewer may have just edited it: never commit over an unsaved or failed save.
    if (!(await flushSave())) { await alertDialog({ title: 'Could not save', message: 'Your latest changes did not save, so nothing was committed. Try again in a moment.' }); return }
    // Re-read which threads ride a milestone right before writing: a thread paired since the draft
    // was opened must not be closed as information-only. If the read fails, stop rather than guess.
    const ridingNow = await loadRiding(threads)
    if (ridingNow === null) {
      await alertDialog({ title: 'Could not check milestones', message: 'Could not confirm which threads are riding a milestone. Nothing was written. Try again in a moment.' })
      return
    }
    const day = meetingDay || new Date().toISOString().slice(0, 10)
    setStep('committing'); setProgress({ done: 0, total: accepted.length })

    // Opened from triage there is no meeting cache. Linking a new thread REPLACES the meeting's
    // thread list, so read the live meeting rather than start from an empty list (which would
    // unlink every thread already on it).
    let meeting = (ctx.meetingCache || []).find(m => m.id === meetingId)
    if (!meeting) {
      try { meeting = await getMeetingById(meetingId) } catch (_) { meeting = null }
      if (!meeting) {
        setStep('review')
        await alertDialog({ title: 'Could not load the meeting', message: 'Nothing was written. Try again in a moment.' })
        return
      }
    }
    const currentIds = getMeetingThreadIds(meeting).slice()
    const touched = []
    const problems = []
    const INFO_NOTE = 'Recorded for information only — no follow-up required.'

    // Batched (Matt, 2026-09-23: commit took minutes). It used to write each item one record at a
    // time, ~6 sequential writes per new thread (thread, opening activity, meeting link, re-read,
    // re-date, follow-up/close). Now four steps, each up to 10 records per request, with the same
    // results: (1) create every new thread, already carrying its Ball in Court (follow-up) or
    // Closed (info only); (2) write every activity in order (Thread Opened dated to the meeting
    // day, Meeting Discussion, Thread Closed with the info-only note); (3) patch existing threads
    // (Ball in Court / Closed + Last Activity); (4) link all new threads to the meeting at once.
    // Nothing here assigns anyone: crewing happens on the Flight Board (RASCI retired).
    const updates = accepted.filter(s => s.kind === 'update' && s.threadId)
    const news = accepted.filter(s => !(s.kind === 'update' && s.threadId))
    setProgress({ done: 0, total: 4 })

    // 1. New threads.
    let created = []
    try {
      created = await createThreadsBatch(news.map(s => ({
        projectId, title: (s.title || 'Untitled topic').trim(), type: s.category || 'Misc. Items',
        description: s.note.trim(), memberId, lastActivity: day,
        extraFields: s.followUp
          ? (s.fuContact ? { 'Ball In Court': [s.fuContact.contactId] } : {})
          : { 'Closed': true },
      })))
    } catch (e) {
      setStep('review')
      await alertDialog({ title: 'Could not create the new threads', message: 'Nothing was written. ' + ((e && e.message) || 'Try again in a moment.') })
      return
    }
    setProgress({ done: 1, total: 4 })

    // 2. Every activity, in order: a thread's opening note before its closing note.
    const acts = []
    news.forEach((s, i) => {
      const rec = created[i]
      if (!rec || !rec.id) return
      acts.push({ threadId: rec.id, type: 'Thread Opened', date: day, notes: s.note.trim(), memberId })
      if (!s.followUp) acts.push({ threadId: rec.id, type: 'Thread Closed', date: day, notes: INFO_NOTE, memberId, closeThread: true })
      touched.push(rec.id)
    })
    updates.forEach(s => {
      acts.push({ threadId: s.threadId, type: 'Meeting Discussion', date: day, notes: s.note.trim(), memberId })
      // Info-only closes, even a thread riding a milestone (Matt, 2026-10-19): marking it "no
      // follow-up" IS the decision, and leaving it open left a discussed thread hanging. Its passenger
      // row self-heals off the milestone (api/passengers.js).
      if (!s.followUp) acts.push({ threadId: s.threadId, type: 'Thread Closed', date: day, notes: INFO_NOTE, memberId, closeThread: true })
      touched.push(s.threadId)
    })
    try {
      const recs = await createThreadActivitiesBatch(acts)
      // Tie each note to THIS meeting and record who held the court at it (database/310), so the
      // minutes bold exactly this meeting's notes and old minutes keep their court.
      const court = {}
      news.forEach((sg, i) => { const rec = created[i]; if (rec && rec.id) court[rec.id] = sg.followUp ? (sg.fuContact || undefined) : null })
      updates.forEach(sg => { court[sg.threadId] = sg.followUp ? (sg.fuContact || undefined) : null })
      const rows = (recs || []).map((r, i) => {
        if (!r || !r.id || !acts[i]) return null
        const c = court[acts[i].threadId]
        return { activityId: r.id, threadId: acts[i].threadId, courtContactId: c ? c.contactId : null, courtName: c === null ? 'Info' : c ? c.name : null }
      }).filter(Boolean)
      if (rows.length) await linkMeetingActivities(meetingId, rows).catch(() => {})
    }
    catch (e) { problems.push('Discussion notes did not all save: ' + ((e && e.message) || 'unknown error')) }
    setProgress({ done: 2, total: 4 })

    // 3. Existing threads: follow-up sets Ball in Court; info-only closes.
    const patches = []
    updates.forEach(s => {
      if (s.followUp) patches.push({ id: s.threadId, fields: { 'Last Activity': day, ...(s.fuContact ? { 'Ball In Court': [s.fuContact.contactId] } : {}) } })
      else patches.push({ id: s.threadId, fields: { 'Closed': true, 'Last Activity': day } })
    })
    try {
      const r = await updateThreadsBatch(patches)
      if (r && r.failed && r.failed.length) problems.push(`${r.failed.length} existing thread${r.failed.length === 1 ? '' : 's'} did not update.`)
    } catch (e) { problems.push('Existing threads did not update: ' + ((e && e.message) || 'unknown error')) }
    setProgress({ done: 3, total: 4 })

    // 4. Link the new threads to this meeting in one write (the link REPLACES the list, so it is
    // the live list plus the new ids).
    // Updated threads may come from earlier meetings (the parser now matches the whole project), so
    // they are linked to this meeting too, or their discussion note would not show in its minutes.
    const newIds = created.map(r => r && r.id).filter(Boolean)
    const linkIds = [...new Set([...currentIds, ...newIds, ...updates.map(s => s.threadId)])]
    if (linkIds.length !== currentIds.length) {
      try { await setMeetingThreads(meetingId, linkIds) }
      catch (e) { problems.push('The threads were written but not all linked to this meeting.') }
    }
    setProgress({ done: 4, total: 4 })

    // 5. Hitch each follow-up to the milestone it rides (the reason a follow-up thread exists). A
    // thread already riding a milestone is left where it is.
    const pairs = []
    const ours = s => !(s.fuContact && !s.fuContact.isTeam)
    news.forEach((s, i) => { const rec = created[i]; if (s.followUp && ours(s) && s.rideMilestoneId && rec && rec.id) pairs.push([rec.id, s.rideMilestoneId]) })
    updates.forEach(s => { if (s.followUp && ours(s) && s.rideMilestoneId && !ridingNow[s.threadId]) pairs.push([s.threadId, s.rideMilestoneId]) })
    let pairFails = 0
    for (const [tid, msId] of pairs) {
      try { await decideThread(memberId, memberName, tid, 'pair', { milestoneId: msId, note: 'From meeting notes' }) }
      // A thread in someone else's court (client, consultant…) is refused on purpose: it never rides a
      // milestone (Matt, 2026-10-19). That's correct, not a failure, so it isn't reported as one.
      catch (e) { if (!/outside the team/i.test(String(e && e.message))) pairFails++ }
    }
    if (pairFails) problems.push(`${pairFails} follow-up${pairFails === 1 ? '' : 's'} could not be hitched to a milestone. They will come up in thread triage instead.`)

    // 5b. Off-agenda threads already handled outside the meeting close with the reason given. Dated
    // today and deliberately NOT linked to this meeting, so they stay out of its minutes (only what
    // was discussed prints there).
    if (handledList.length) {
      const today = new Date().toISOString().slice(0, 10)
      try {
        await createThreadActivitiesBatch(handledList.map(h => ({ threadId: h.threadId, type: 'Thread Closed', date: today, notes: 'Handled outside the meeting: ' + h.note, memberId, closeThread: true })))
        const r = await updateThreadsBatch(handledList.map(h => ({ id: h.threadId, fields: { 'Closed': true, 'Last Activity': today } })))
        if (r && r.failed && r.failed.length) problems.push(`${r.failed.length} handled thread${r.failed.length === 1 ? '' : 's'} did not close.`)
        handledList.forEach(h => { if (ctx.threadActCache) ctx.threadActCache[h.threadId] = undefined })
      } catch (e) { problems.push('The threads handled outside the meeting did not close: ' + ((e && e.message) || 'unknown error')) }
    }

    // Latch the meeting as imported so the Import action disables itself — re-running the same
    // summary would create duplicate threads. Best-effort; never blocks the commit result.
    if (touched.length) { try { await markMeetingNotesImported(meetingId) } catch (_) {} }
    if (touched.length || handledList.length) { try { await commitMeetingNotesDraft(draftId, memberName, { getToken }) } catch (_) {} }

    // 6. Post the minutes (Matt, 2026-10-20): committing is posting. The minutes lock, every attendee
    // is emailed the PDF, and a copy goes on the Client Hub silently when a client attended (2026-10-28).
    // The threads are already written, so a failure here is reported, never undone.
    let posted = null
    if (touched.length) {
      setProgress({ done: 5, total: 4 })
      // audience:'auto': every attendee with an email gets the PDF (Matt, 2026-10-28); the Client Hub
      // copy only when a client on the Hub attended.
      const post = await postMeetingMinutes(meetingId, { send: false, audience: 'auto', getToken, postingMemberId: memberId, cc })
      if (!post || post.error) problems.push('The threads were written, but the minutes did not post: ' + ((post && post.error) || 'unknown error') + '. Post them from the meeting menu.')
      else {
        posted = { internal: !!post.internal, due: post.revisionDueLabel || '', hub: false, emailed: post.emailedNames || [], cc: post.cc || [], noEmail: post.noEmail || [] }
        if (post.clientPresent) {
          try {
            await addMinutesToClientHub({ meetingId, projectId: projectId || meetingProjectId, projectName: meetingProjectName || projectName, published: true, post, postedBy: { memberId, name: memberName } })
            posted.hub = true
          } catch (e) { problems.push('The minutes posted, but did not reach the Client Hub: ' + ((e && e.message) || 'unknown error') + '. Add them from the Client Hub branch.') }
        }
      }
    }

    // Bust caches so the new activity/threads show, then let the parent reload.
    ctx.threadCache = undefined
    if (ctx.threadActCache) touched.forEach(id => { ctx.threadActCache[id] = undefined })
    window.dispatchEvent(new Event('outline-render'))
    onCommitted && onCommitted('committed')
    if (problems.length) await alertDialog({ title: 'Imported with issues', message: problems.join('\n') })
    else if (posted) await alertDialog({
      title: 'Minutes posted',
      message: [
        'The minutes are posted.' + (posted.hub ? ' A copy is on the Client Hub.' : '') +
          (posted.due ? ' Revisions can be requested until ' + posted.due + '.' : ''),
        posted.emailed.length ? `Emailed with the PDF attached: ${nameList(posted.emailed)}${posted.cc.length ? ', cc ' + nameList(posted.cc) : ''}. Replies come to you.` : '',
        posted.noEmail.length ? `No email on file for ${nameList(posted.noEmail)}: send theirs by hand.` : '',
      ].filter(Boolean).join(' '),
    })
    onClose && onClose()
  }

  // An off-agenda thread may not be in the meeting's own riding read: check both.
  const ridingOf = id => riding[id] || offRiding[id] || null
  const isRiding = it => it.kind === 'update' && !!ridingOf(it.threadId)
  const missingPeople = people.filter(n => !matchAttendee(attendees, n))

  /* ── render ── */
  return createPortal(
    <div style={zIndex ? { ...overlay, zIndex } : overlay}>
      <div data-modal-id="MeetingNotesImportModal" style={card} role="dialog" aria-modal="true">
        <div style={head}>
          <div style={{ minWidth: 0 }}>
            <div style={title}>Meeting notes</div>
            <div style={meetingLine}>
              {[meetingProjectName || projectName || (meetingInfo && meetingInfo.projectNumber), meetingInfo && meetingInfo.type, meetingInfo && meetingInfo.date].filter(Boolean).join(' · ') || 'Import meeting notes'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {step === 'review' && !readOnly && draftId && (
              saveState.s === 'error' ? (
                <span style={{ ...saveLine, color: '#B3261E' }}>
                  Not saved
                  <button onClick={() => flushSave()} style={retryBtn}>Retry</button>
                </span>
              ) : (
                <span style={saveLine}>
                  {saveState.s === 'saving' || saveState.s === 'pending' ? 'Saving…'
                    : saveState.at ? 'Saved ' + saveState.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    : 'Saved'}
                </span>
              )
            )}
            {step !== 'committing' && <button onClick={handleClose} style={xBtn}>×</button>}
          </div>
        </div>

        {step === 'loading' && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13 }}>Loading…</div>
        )}

        {step === 'paste' && (
          <div style={{ padding: '4px 26px 20px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            <div style={fieldLabel}>Paste the Zoom "Meeting Assets" summary</div>
            <AutoTextarea value={text} onChange={e => setText(e.target.value)} autoFocus minHeight={160}
              placeholder="Paste the written meeting summary from Zoom's AI Companion email here…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }} />
            <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 8 }}>
              We'll suggest new threads and updates to existing ones. Nothing is written to threads until it's reviewed and committed — you can close this and come back later.
            </div>
          </div>
        )}

        {step === 'review' && (
          <div style={{ padding: '4px 26px 20px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {reviewNote && !readOnly && (isWaitingApprover
              ? <div style={noteBanner}>Sent back to {draftedBy || 'the drafter'} to fix: {reviewNote} It comes to you for approval once fixed, or make the changes and commit it yourself now.</div>
              : <div style={noteBanner}>Sent back to you to fix: {reviewNote}</div>
            )}
            <div style={infoBox}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div style={{ ...fieldLabel, marginBottom: 0 }}>The meeting</div>
                {!readOnly && <button onClick={() => setEditingMeeting(true)} style={{ ...editMeetingBtn, marginTop: 0, marginLeft: 'auto' }}>Edit</button>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--aj-text)', marginTop: 6 }}>
                {[meetingInfo && meetingInfo.type, meetingInfo && meetingInfo.date, meetingInfo && meetingInfo.time].filter(Boolean).join(' · ') || 'Meeting'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginTop: 2 }}>
                {[[meetingInfo && meetingInfo.projectNumber, meetingProjectName].filter(Boolean).join(' '), meetingInfo && meetingInfo.location].filter(Boolean).join(' · ')}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {attendees.length ? attendees.map(a => <span key={a.contactId || a.name} style={personChip}>{a.name}</span>)
                  : <span style={{ ...personChip, color: '#B3261E', borderColor: '#E8B4B0' }}>No attendees listed</span>}
              </div>
              {(() => {
                const covered = new Set(items.filter(i => i.threadId).map(i => i.threadId))
                const left = offAgenda.filter(t => !covered.has(t.id))
                const fmt = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                const order = { discussed: 0, touched: 1, not: 2 }
                const sorted = [...left].sort((a, b) => (order[a.verdict] ?? 3) - (order[b.verdict] ?? 3))
                const came = left.filter(t => t.verdict === 'discussed' || t.verdict === 'touched').length
                return left.length > 0 && (
                  <div style={offBox}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)' }}>
                      {offState === 'checked'
                        ? (came ? `${came} of ${left.length} off-agenda thread${left.length === 1 ? '' : 's'} came up in these notes` : `None of the ${left.length} off-agenda threads came up in these notes`)
                        : (left.length === 1 ? 'One open thread wasn\u2019t on the agenda' : `${left.length} open threads weren\u2019t on the agenda`)}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)', margin: '3px 0 8px', lineHeight: 1.45 }}>
                      {offState === 'checking' ? 'Reading the notes for each one…'
                        : offState === 'failed' ? 'Couldn\u2019t read the notes for these. Each involves someone at this meeting; add one if it came up.'
                        : 'Each involves someone who was at this meeting. Add the ones that came up; the rest come back at the next meeting with them.'}
                    </div>
                    {sorted.map(t => {
                      const hit = t.verdict === 'discussed' || t.verdict === 'touched'
                      return (
                        <div key={t.id} style={offRow}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{t.title}</span>
                              {t.verdict && <span style={{ fontSize: 11.5, fontWeight: 600, color: t.verdict === 'discussed' ? '#1f7a4d' : t.verdict === 'touched' ? '#b7791f' : 'var(--aj-text-muted)' }}>
                                {t.verdict === 'discussed' ? 'Came up' : t.verdict === 'touched' ? 'Mentioned in passing' : 'Not in the notes'}
                              </span>}
                            </div>
                            {t.about && <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 2, lineHeight: 1.4 }}>{t.about}</div>}
                            <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 2 }}>
                              {[(t.involvedHere && t.involvedHere.length) ? 'Involves ' + t.involvedHere.join(', ') + ', who ' + (t.involvedHere.length === 1 ? 'was' : 'were') + ' here' : null,
                                t.court ? 'in ' + t.court + '\u2019s court' : null,
                                offRiding[t.id] ? 'riding ' + (offRiding[t.id].milestoneName || 'a milestone') : 'not riding a milestone',
                                t.lastMeeting ? 'last discussed ' + fmt(t.lastMeeting) : 'never discussed at a meeting'].filter(Boolean).join(' · ')}
                            </div>
                            {hit && t.quote && <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 5, paddingLeft: 8, borderLeft: '2px solid var(--aj-border-mid)', fontStyle: 'italic' }}>“{t.quote}”</div>}
                            {hit && t.note && <div style={{ fontSize: 12, color: 'var(--aj-text)', marginTop: 4 }}>Note to add: {t.note}</div>}
                          </div>
                          {handled[t.id] ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, maxWidth: 220 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#1f7a4d' }}>Closes on commit</span>
                              <span style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', textAlign: 'right' }}>{handled[t.id].note}</span>
                              {!readOnly && <button style={skipBtn} onClick={() => setHandledFor(t, null)}>Undo</button>}
                            </div>
                          ) : !readOnly && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                              <button style={hit ? { ...offBtn, background: '#07272D', color: '#fff', border: '0.5px solid #07272D' } : offBtn} onClick={() => addItemFor(t)}>{hit ? 'Add this note' : 'It came up'}</button>
                              <button style={offBtn} onClick={() => askHandled(t)} title="It was dealt with outside this meeting: close it so it stops coming back">Already handled</button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
              {missingPeople.length > 0 && (
                <div style={missingNote}>
                  The notes show {missingPeople.join(', ')} at this meeting, but the attendee list does not have {missingPeople.length === 1 ? 'them' : 'them'}.{isReviewer ? ' Send it back to have them added.' : ' Add them with Edit meeting info (create the contact first if they are new).'}
                </div>
              )}
            </div>
            {readOnly && (
              <div style={reviewBanner}>Waiting for {savedReviewerName ? savedReviewerName + ' to review it' : 'review'}. Locked while it waits; reopen it if you need to change something first.</div>
            )}
            {isReviewer && (
              <div style={reviewBanner}>Sent to you for review. Edit, skip, or add anything you want, then commit it to threads.</div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '18px 0 10px' }}>
              <div style={sectionHead}>What was discussed</div>
              <div style={{ fontSize: 12.5, color: 'var(--aj-text-muted)' }}>
                {items.filter(i => i.accept).length} of {items.length} included{readOnly || isReviewer ? '' : ' · edit, split, skip or add'}
              </div>
            </div>
            {items.map(it => (
              <div key={it._id} style={it.accept ? sugg : { ...sugg, background: 'transparent', borderStyle: 'dashed' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: it.accept ? 8 : 0 }}>
                  {/* kind: new thread vs an update to one already on this meeting */}
                  {it.accept
                    ? <Segmented value={it.kind} disabled={readOnly} onChange={v => patch(it._id, { kind: v })}
                        options={[{ value: 'new', label: 'New thread' }, { value: 'update', label: 'Update a thread', disabled: !threads.length }]} />
                    : <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--aj-text-muted)' }}>{it.kind === 'update' ? 'Update' : 'New thread'}</span>}
                  {!it.accept && (
                    <span style={{ fontSize: 12, color: 'var(--aj-text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Skipped: {(it.kind === 'update' ? (threads.find(t => t.id === it.threadId) || {}).title : it.title) || 'untitled'} · won't be written
                    </span>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {it.accept && it.confidence && (
                      <span title={CONFIDENCE_HELP[it.confidence] || CONFIDENCE_HELP.medium}
                        style={{ fontSize: 10, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'help', borderBottom: '0.5px dotted var(--aj-text-muted)' }}>
                        {it.confidence} confidence
                      </span>
                    )}
                    {!readOnly && it.accept && (
                      <button onClick={() => splitItem(it._id)} style={skipBtn} title="Two people own a next move here: make a second thread so each has its own court">Split</button>
                    )}
                    {!readOnly && (it.manual
                      ? <button onClick={() => removeItem(it._id)} style={skipBtn} title="Remove this item you added">Remove</button>
                      : <button onClick={() => patch(it._id, { accept: !it.accept })} style={skipBtn} title={it.accept ? 'Leave this item out of the import' : 'Put this item back in'}>{it.accept ? 'Skip' : 'Include'}</button>)}
                  </div>
                </div>

                {it.accept && <>

                {it.splitFrom != null && (
                  <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 8 }}>Split from the item above. Trim each note to its own half and pick each one's person, so each thread has one court.</div>
                )}

                {it.kind === 'update' ? (
                  <div style={{ marginBottom: 8 }}>
                    <div style={fieldLabel}>Thread</div>
                    <SearchSelect value={it.threadId} disabled={readOnly} onChange={v => patch(it._id, { threadId: v })} style={input}
                      options={[{ value: '', label: '— pick a thread —' }, ...threads.map(t => ({ value: t.id, label: t.title + (t.type ? ' · ' + t.type : '') + (riding[t.id] ? ' · riding ' + (riding[t.id].milestoneName || 'a milestone') : '') }))]} />
                    {ridingOf(it.threadId) && (
                      <div style={ridingNote}>Riding milestone: {ridingOf(it.threadId).milestoneName || 'a milestone'}</div>
                    )}
                    {it.prior && (it.prior.about || it.prior.quote) && (
                      <div style={priorBox}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text-muted)', marginBottom: 3 }}>
                          {['Before this meeting', it.prior.lastMeeting ? 'last discussed ' + new Date(it.prior.lastMeeting + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null, it.prior.court ? 'in ' + it.prior.court + '’s court' : null].filter(Boolean).join(' · ')}
                        </div>
                        {it.prior.about && <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.45 }}>{it.prior.about}</div>}
                        {it.prior.quote && <div style={{ fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 5, paddingLeft: 8, borderLeft: '2px solid var(--aj-border-mid)', fontStyle: 'italic' }}>In these notes: “{it.prior.quote}”</div>}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 2 }}>
                      <div style={fieldLabel}>Title</div>
                      <input value={it.title} disabled={readOnly} onChange={e => patch(it._id, { title: e.target.value })} style={input} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={fieldLabel}>Category</div>
                      <SearchSelect value={it.category} disabled={readOnly} onChange={v => patch(it._id, { category: v })} style={input}
                        options={cats.map(c => ({ value: c, label: c }))} />
                    </div>
                  </div>
                )}

                <div style={fieldLabel}>Discussion note{it.ballInCourt ? ` · ball in court: ${it.ballInCourt}` : ''}</div>
                <AutoTextarea value={it.note} disabled={readOnly} onChange={e => patch(it._id, { note: e.target.value })} minHeight={40}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '0.5px solid var(--aj-border)', fontFamily: '"DM Sans",sans-serif', fontSize: 14, lineHeight: 1.5, color: 'var(--aj-text)', background: readOnly ? 'var(--aj-bg-muted)' : 'var(--aj-bg-subtle)' }} />

                {/* Follow-up: tracked item (assign a person) vs info-only (recorded & closed). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  {/* An update reads as still-open-or-resolved (Matt, 2026-10-22, the thread's own Open/Closed and Ball in Court words): "info only" made a resolved
                      follow-up look like it was never real. */}
                  <Segmented value={it.followUp ? 'owed' : 'done'} disabled={readOnly} onChange={v => patch(it._id, { followUp: v === 'owed' })}
                    options={it.kind === 'update'
                      ? [{ value: 'owed', label: 'Still open' }, { value: 'done', label: 'Resolved' }]
                      : [{ value: 'owed', label: 'Follow-up' }, { value: 'done', label: 'Info only' }]} />
                  {it.followUp ? (
                    <>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text-mid)' }}>Ball in court</span>
                      <FollowUpPicker value={it.fuContact} attendees={attendees} disabled={readOnly} invalid={it.accept && !it.fuContact} onPick={c => patch(it._id, { fuContact: c })} />
                    </>
                  ) : isRiding(it) ? (
                    <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>The note is recorded and the thread closes, so {ridingOf(it.threadId).milestoneName || 'its milestone'} no longer waits on it.</span>
                  ) : it.kind === 'update' ? (
                    <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>The note is recorded and the thread closes.</span>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Recorded and closed, no follow-up.</span>
                  )}
                </div>
                {it.followUp && it.accept && !it.fuContact && attendees.length > 0 && (
                  <div style={{ fontSize: 11.5, color: '#B3261E', marginTop: 6 }}>{it.kind === 'update' ? 'Pick who has the ball, or choose Resolved.' : 'Pick who follows up, or choose Info only.'}</div>
                )}
                {/* Only a thread in OUR court rides a milestone (Matt, 2026-10-19; docs/PASSENGERS.md "Only
                    our court rides"). Someone outside the team's follow-up is theirs: it comes back on
                    the agenda of the next meeting with them instead. */}
                {it.followUp && it.fuContact && !it.fuContact.isTeam && (
                  <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 6 }}>
                    In {it.fuContact.name}{'\u2019'}s court, so it doesn{'\u2019'}t ride a milestone{isRiding(it) ? ` (it comes off ${ridingOf(it.threadId).milestoneName || 'its milestone'})` : ''}. It comes back on the agenda of the next meeting with them.
                  </div>
                )}
                {it.followUp && !(it.fuContact && !it.fuContact.isTeam) && !isRiding(it) && milestones.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--aj-text-mid)', flexShrink: 0 }}>Rides milestone</span>
                    <SearchSelect value={it.rideMilestoneId || ''} disabled={readOnly} onChange={v => patch(it._id, { rideMilestoneId: v })} style={{ ...input, flex: 1 }}
                      options={[{ value: '', label: 'None yet (goes to thread triage)' }, ...milestones.map(m => ({ value: m.id, label: m.name + (m.inProgress ? ' (in progress)' : '') }))]} />
                  </div>
                )}
                {it.followUp && isRiding(it) && it.fuContact && it.fuContact.isTeam && (
                  <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 6 }}>It keeps riding {ridingOf(it.threadId).milestoneName || 'its milestone'}, which can{'\u2019'}t post until {it.fuContact.name} resolves it.</div>
                )}
                </>}
              </div>
            ))}
            {!readOnly && (
              <button onClick={addItem} style={addBtn}>+ Add item</button>
            )}
            {/* THE DECISION (2026-10-16, CLAUDE.md "Triage decisions"): what happens to this import. */}
            {!readOnly && (
              <div style={{ marginTop: 18 }}>
                {/* Reading the work is not a decision, so it sits above the tiles as a link. */}
                <button onClick={doPreviewMinutes} disabled={previewing} style={previewLink}>
                  {previewing ? 'Preparing the minutes…' : 'Preview the minutes as they will post ↗'}
                </button>
                <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' }}>What happens to these notes</div>
                <TriageTiles options={decisionOptions()} onConfirm={async (key, v) => { const o = decisionOptions().find(x => x.key === key); await o.run(v); return o.done ? o.done(v) : 'Done.' }} />
              </div>
            )}
          </div>
        )}

        {step === 'committing' && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--aj-text-mid)', fontSize: 13 }}>
            {COMMIT_STEPS[progress.done] || 'Committing…'}
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--aj-text-muted)' }}>Keep this window open until it finishes. It closes on its own.</div>
          </div>
        )}

        <div style={foot}>
          {step !== 'committing' && <button onClick={handleClose} style={ghostBtn}>Close</button>}
          {step === 'paste' && <button onClick={doParse} style={primaryBtn} disabled={parsing || !text.trim()}>{parsing ? 'Parsing…' : 'Parse notes'}</button>}

          {step === 'review' && !readOnly && !isReviewer && <button onClick={() => setStep('paste')} style={ghostBtn}>Back</button>}
          {step === 'review' && readOnly && <>
            <button onClick={doReopen} style={ghostBtn}>Reopen for edits</button>
            {!sentBackBy && <button onClick={doCommit} style={primaryBtn}>Commit {items.filter(i => i.accept).length} to threads</button>}
          </>}
        </div>
      </div>
      {editingMeeting && (
        <CreateMeetingModal
          projectId={projectId}
          meetingId={meetingId}
          zIndex={(zIndex || 600) + 10}
          onClose={() => setEditingMeeting(false)}
          onSaved={onMeetingSaved}
        />
      )}
    </div>,
    document.body
  )
}

// What each commit step is doing, in plain words (Matt, 2026-10-15: "step 0 of 4" said nothing).
const COMMIT_STEPS = [
  'Creating the new threads…',
  'Writing the meeting notes onto each thread…',
  'Updating the existing threads…',
  'Linking the threads to this meeting…',
  'Attaching follow-ups to their milestones…',
  'Posting the minutes…',
]

const previewLink = { display: 'block', margin: '0 0 12px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-accent)', textDecoration: 'underline', textUnderlineOffset: 3 }
// The triage-card language (TriageTiles, the Operations Director Triage): a blurred backdrop, a calm
// page-colored sheet, large type, soft cards, two-way choices as segmented controls (2026-10-22).
const overlay = { position: 'fixed', inset: 0, background: 'rgba(4,21,24,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px', overflowY: 'auto' }
const card = { width: '100%', maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-page)', borderRadius: 18, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', overflow: 'hidden' }
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '20px 26px 16px', flexShrink: 0 }
const infoBox = { border: '0.5px solid var(--aj-border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12, background: 'var(--aj-bg-elevated)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }
const personChip = { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }
const sectionHead = { fontFamily: '"DM Sans",sans-serif', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--aj-text)' }
const offBox = { marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'var(--aj-bg-subtle, #f4f7f8)', border: '0.5px solid var(--aj-border)' }
const offRow = { display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderTop: '0.5px solid var(--aj-border)' }
const offBtn = { flexShrink: 0, padding: '7px 12px', borderRadius: 10, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const missingNote = { fontSize: 12, color: '#7A4E12', background: '#FFF4E5', border: '0.5px solid #E8B978', borderRadius: 4, padding: '6px 8px', marginTop: 8 }
const meetingLine = { fontFamily: '"DM Sans",sans-serif', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--aj-text)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
// Hover text for the parser's per-item confidence (api/meeting-notes-parse.js returns high|medium|low).
const CONFIDENCE_HELP = {
  high: 'How sure the parser is that it read this item correctly: the topic, and for an update, which thread it belongs to. High: it is confident. Still worth a quick read.',
  medium: 'How sure the parser is that it read this item correctly: the topic, and for an update, which thread it belongs to. Medium: check the thread match and the note before committing.',
  low: 'How sure the parser is that it read this item correctly: the topic, and for an update, which thread it belongs to. Low: it was unsure. Read this one closely, it may be the wrong thread or a merged topic.',
}
const title = { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.14em', fontSize: 11, fontWeight: 700, color: 'var(--aj-text-muted)' }
const xBtn = { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' }
const foot = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 26px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0, background: 'var(--aj-bg-page)' }
const fieldLabel = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text-muted)', marginBottom: 5 }
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: '0.5px solid var(--aj-border)', fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const miniSelect = { padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const sugg = { border: '0.5px solid var(--aj-border)', borderRadius: 14, padding: '14px 16px 16px', marginBottom: 12, background: 'var(--aj-bg-elevated)', boxShadow: '0 1px 2px rgba(7,39,45,0.04)' }
const primaryBtn = { background: '#07272D', border: 'none', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: '#fff' }
const ghostBtn = { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text)' }
const dangerGhostBtn = { ...ghostBtn, color: '#B3261E', marginRight: 'auto' }
const noteBanner = { background: '#FFF4E5', border: '0.5px solid #E8B978', borderRadius: 6, padding: '8px 10px', fontSize: 12, color: '#7A4E12', marginBottom: 10 }
const saveLine = { display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' }
const retryBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: '#B3261E', textDecoration: 'underline' }
const skipBtn = { background: 'none', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text-mid)' }
const addBtn = { width: '100%', background: 'transparent', border: '0.5px dashed var(--aj-border-mid)', borderRadius: 14, padding: '14px 12px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--aj-text-mid)' }
const editMeetingBtn = { background: 'none', border: 'none', padding: 0, marginTop: 4, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-accent-blue)', textDecoration: 'underline' }
const priorBox = { marginTop: 8, padding: '8px 10px', borderRadius: 10, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)' }
const ridingNote ={ fontSize: 11.5, color: '#1D6FB8', marginTop: 5 }
const reviewBanner = { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }

/* A two-or-three-way choice as a segmented control (the one-tap replacement for a dropdown or a
   checkbox whose meaning depends on its label). */
function Segmented({ value, options, onChange, disabled }) {
  return (
    <div role="radiogroup" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: 10, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)' }}>
      {options.map(o => {
        const on = o.value === value
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} disabled={disabled || o.disabled}
            onClick={() => !on && onChange(o.value)}
            style={{ border: 'none', borderRadius: 8, padding: '6px 12px', cursor: disabled || o.disabled ? 'default' : 'pointer',
              fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600,
              background: on ? 'var(--aj-bg-elevated)' : 'transparent', color: on ? 'var(--aj-text)' : 'var(--aj-text-muted)',
              boxShadow: on ? '0 1px 3px rgba(7,39,45,0.12)' : 'none', opacity: o.disabled ? 0.45 : 1, transition: 'background .15s, color .15s' }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
